import {
  documentToReactComponents,
  Options,
} from "@contentful/rich-text-react-renderer";
import { BLOCKS, Document } from "@contentful/rich-text-types";
import { theme as defaultTheme, Spacing } from "@mindlercare/ui-web";
import { ReactNode } from "react";
import { styled } from "styled-components";

import { ContentfulEntryLink, ContentfulRichContentLinks } from "../types";
import { IcbtAudioPlayer, isAudioAsset } from "./IcbtAudioPlayer";
import { IcbtExerciseLabel } from "./IcbtExerciseLabel";
import { IcbtVideoPlayer, isVideoAsset } from "./IcbtVideoPlayer";

interface ContentfulRichTextProps {
  links?: ContentfulRichContentLinks;
  document?: Document;
  enableAudio?: boolean;
  enableVideo?: boolean;
  renderQuestionnaire?: (entry: ContentfulEntryLink) => ReactNode;
  renderPatientNote?: (entry: ContentfulEntryLink) => ReactNode;
}

type ContentfulOptions = {
  enableAudio?: boolean;
  enableVideo?: boolean;
  renderQuestionnaire?: (entry: ContentfulEntryLink) => ReactNode;
  renderPatientNote?: (entry: ContentfulEntryLink) => ReactNode;
};

const supportedImageMimeTypes = ["image/png", "image/jpeg", "image/svg+xml"];

export const contentfulOptions = (
  links?: ContentfulRichContentLinks,
  options?: ContentfulOptions,
): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => {
      return children?.toString() === "" ? null : (
        <Paragraph>{children}</Paragraph>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      if (!links) return null;

      const entryId = node.data.target.sys.id;
      const entry = links.entries.block.find(
        ({ sys: { id } }) => id === entryId,
      );

      if (entry?.__typename === "GenericCard") {
        return (
          <ReusableContent>
            {documentToReactComponents(
              entry.content.json,
              contentfulOptions(entry.content.links, options),
            )}
          </ReusableContent>
        );
      }

      if (entry?.__typename === "ReusableContent") {
        return documentToReactComponents(
          entry.content.json,
          contentfulOptions(entry.content.links, options),
        );
      }

      if (entry?.__typename === "IcbtExerciseLabel") {
        return (
          <div>
            <IcbtExerciseLabel {...entry} />
            <Spacing b={8} />
          </div>
        );
      }

      if (
        entry?.__typename === "Questionnaire" &&
        options?.renderQuestionnaire
      ) {
        return options.renderQuestionnaire(entry);
      }

      if (
        entry?.__typename === "PatientIcbtNote" &&
        options?.renderPatientNote
      ) {
        return options.renderPatientNote(entry);
      }

      return null;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      if (!links) return null;

      const assetId = node.data.target.sys.id;
      const asset = links.assets.block.find(
        ({ sys: { id } }) => id === assetId,
      );

      if (asset && supportedImageMimeTypes.includes(asset?.contentType)) {
        return (
          <ImageWrapper>
            <Image src={asset?.url} alt={asset?.description} />
          </ImageWrapper>
        );
      }

      if (options?.enableAudio && asset && isAudioAsset(asset)) {
        return <IcbtAudioPlayer url={asset.url} />;
      }

      if (options?.enableVideo && asset && isVideoAsset(asset)) {
        return <IcbtVideoPlayer url={asset.url} />;
      }

      return null;
    },
    [BLOCKS.QUOTE]: (_, children) => {
      return <HighlightSection>{children}</HighlightSection>;
    },
  },
});

export const ContentfulRichText = ({
  document,
  links,
  enableAudio,
  enableVideo,
  renderQuestionnaire,
  renderPatientNote,
}: ContentfulRichTextProps): JSX.Element | null => {
  if (!document) {
    return null;
  }

  return (
    <ContentfulRichTextWrapper>
      {documentToReactComponents(
        document,
        contentfulOptions(links, {
          enableAudio,
          enableVideo,
          renderQuestionnaire,
          renderPatientNote,
        }),
      )}
    </ContentfulRichTextWrapper>
  );
};

const Text = styled.p`
  color: ${defaultTheme.COLORS.BLACK_GREY};
`;

const ContentfulRichTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${defaultTheme.SPACING.SM};
`;

const Paragraph = styled(Text)``;

const ImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  border-radius: 14px;
  margin: 0 0 15px 0;
  padding: 0;
`;

const Image = styled.img`
  max-width: 370px;
  min-width: 170px;
`;

const HighlightSection = styled.div`
  padding: ${defaultTheme.PADDING.MD};
  background-color: ${defaultTheme.COLORS.PRIMARY_X_LIGHT};
  justify-content: center;
  margin-bottom: ${defaultTheme.PADDING.MD};
  & > ${Paragraph} {
    padding: 0px;
  }
`;

const ReusableContent = styled.div`
  background-color: ${defaultTheme.COLORS.WARM_LIGHT};
  padding: ${defaultTheme.SPACING.MD};
  border-radius: ${defaultTheme.BORDER_RADIUS.SM};
`;
